import { EzEnumeration2 } from '/ezlibrary/enums/EzEnumeration2.js';

import { EzString } from '/ezlibrary/helpers/EzString.js';
import { EzHtml } from '/ezlibrary/helpers/EzHtml.js';

import {
    EzNovaAuthenticationProviderId,
    EzIntegrationType
} from '/secure/integrations/ez-integration-enums.js';

/**
 * @class
 * @extends {EzEnumeration2}
 * @description
 * Javascript enumeration equivlant of Java's enumeration EzIntegrationProviderId.java
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with
 *     import { EzIntegrationProviderId } from '/secure/integrations/EzIntegrationProviderId.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Imported by:
 *  1) /ezlibrary/enums/EzEnumerations.js
 *  2) /ezlibrary/EzClockerContext/ez-context.js
 *  3) /secure/employer/employer-dashboard-controller.js
 *  4) /secure/widgets/EzExportReportDialog/ez-export-report-dialog.js
 *  5) /secure/integrations/EzIntegrationProviderId.js
 *  6) /secure/integration/ez-integration-controller.js
 *  7) /secure/integrations/ez-integrations-employee-linker-controller.js
 *  8) /secure/integrations/ez-integrations-time-entry-exporter.js
 *  9) /secure/integrations/ez-integration-wizard-common.js
 *  10) /secure/integrations/ez-integration-wizard-context.js
 *  11) /secure/integrations/ez-integration-wizard-state-loader.js
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzIntegrationProviderId extends EzEnumeration2 {
    /**
     * @static
     * @public @method
     * Gets the integrations display name.
     * @param {string} enumPropertyName
     * A valid enum property name for EzIntegrationProviderId
     * @returns {string}
     */
    static ezToDisplayValue(enumPropertyName) {
        return EzString.stringOrEmpty(
            EzIntegrationProviderId.ezEnumData(enumPropertyName)?.displayName);
    }

    /**
     * @static
     * @public @method
     * Gets the integration's setup dialog name as registered with ezApi
     * The result is normally used to get the reference of the dialog's
     * class from ezApi.
     *  Example: globalThis.ezApi.ezclocker?.[{setup_dialog_api_name}]
     * @param {string} enumPropertyName
     * A valid enum property name for EzIntegrationProviderId
     * @returns {string}
     */
    static ezToIntegrationSetupDialogApiName(enumPropertyName) {
        return EzString.stringOrEmpty(
            EzIntegrationProviderId.ezEnumData(enumPropertyName)?.ezIntegrationSetupDialogApiName);
    }

    /**
     * @static
     * @public @method
     * Gets the integration's application/company website.
     * @param {string} enumPropertyName
     * A valid enum property name for EzIntegrationProviderId
     * @returns {string}
     */
    static ezToIntegrationHomeUrl(enumPropertyName) {
        return EzString.stringOrEmpty(
            EzIntegrationProviderId.ezEnumData(enumPropertyName)?.ezIntegrationHomeUrl);
    }

    /**
     * @static
     * @public @method
     * Gets the url to the logo that can be used for the integration.
     * Some logos we cannot use without permission so the result could
     * be a generic image or none at all.
     * @param {string} enumPropertyName
     * A valid enum property name for EzIntegrationProviderId
     * @returns {string}
     */
    static ezToIntegrationLogoUrl(enumPropertyName) {
        return EzString.stringOrEmpty(
            EzIntegrationProviderId.ezEnumData(enumPropertyName)?.ezIntegrationLogoUrl);
    }

    /**
     * @static
     * @public @method
     * Gets the text that is used along with the integration logo to
     * identify the integration.
     * @param {string} enumPropertyName
     * A valid enum property name for EzIntegrationProviderId
     * @returns {string}
     */
    static ezToIntegrationLogoText(enumPropertyName) {
        return EzString.stringOrEmpty(
            EzIntegrationProviderId.ezEnumData(enumPropertyName)?.ezIntegrationLogoText);
    }

    /**
     * @static
     * Obtains the enumeration's ezIntegrationSubTitleText data value.
     * @public @method
     * @param {string} enumPropertyName
     * @returns {string}
     */
    static ezToIntegrationSubTitleText(enumPropertyName) {
        return EzString.stringOrEmpty(
            EzIntegrationProviderId.ezEnumData(enumPropertyName)?.ezIntegrationSubTitleText);
    }

    /**
     * @static
     * @public @method
     * Gets the root HTML element id for the integration's ezClocker setup dialog.
     * @param {string} enumPropertyName
     * A valid enum property name for EzIntegrationProviderId
     * @returns {string}
     */
    static ezToIntegrationSetupDialogId(enumPropertyName) {
        return EzString.stringOrEmpty(
            EzIntegrationProviderId.ezEnumData(enumPropertyName)?.ezIntegrationSetupDialogId);
    }

    /**
     * @static
     * @public @method
     * Gets the name of the product that ezClocker is integrating with.
     * @param {string} enumPropertyName
     * A valid enum property name for EzIntegrationProviderId
     * @returns {string}
     */
    static ezToIntegrationProductName(enumPropertyName) {
        return EzString.stringOrEmpty(
            EzIntegrationProviderId.ezEnumData(enumPropertyName)?.ezIntegrationProductName);
    }

    /**
     * @static
     * @public @method
     * Gets the home website of the specific product Ezclocker is integrating with.
     * @param {string} enumPropertyName
     * A valid enum property name for EzIntegrationProviderId
     * @returns {string}
     */
    static ezToIntegrationProductHomeUrl(enumPropertyName) {
        return EzString.stringOrEmpty(
            EzIntegrationProviderId.ezEnumData(enumPropertyName)?.ezIntegrationProductHomeUrl);
    }

    /**
     * @static
     * @public @method
     * Gets the HTML 'notes' relating to the integration for use in ezClocker's UX
     * @param {string} enumPropertyName
     * A valid enum property name for EzIntegrationProviderId
     * @returns {string}
     */
    static ezToIntegrationSetupNotes(enumPropertyName) {
        return EzString.stringOrEmpty(EzIntegrationProviderId.ezEnumData(enumPropertyName)?.ezIntegrationSetupNotes);
    }

    /**
     * @static
     * @public @method
     * Gets the integration's help url relating to integrations.
     * Most of the time, this is an external site not maintained or owned by Ezclocker.
     * @param {string} enumPropertyName
     * A valid enum property name for EzIntegrationProviderId
     * @returns {string}
     */
    static ezToIntegrationHelpUrl(enumPropertyName) {
        return EzString.stringOrEmpty(EzIntegrationProviderId.ezEnumData(enumPropertyName)?.ezIntegrationHelpUrl);
    }

    /**
     * @static
     * @public @method
     * Gets ezClocker's EzNovaAuthenticationProviderId for the provided enumPropertyName (EzIntegrationProviderId)
     * @param {string} enumPropertyName
     * A valid enum property name from EzIntegrationProviderId
     * @returns {string}
     * A valid enum property value from EzNovaAuthenticationProviderId
     */
    static ezToEzNovaAuthenticationProviderId(enumPropertyName) {
        return EzNovaAuthenticationProviderId.ezValueOf(
            EzString.stringOrDefault(
                EzIntegrationProviderId.ezEnumData(enumPropertyName)?.ezNovaAuthenticationProviderId,
                EzNovaAuthenticationProviderId.UNKNOWN));
    }

    /**
     * @static
     * @public @method
     * Gets EzIntegrationType for the provided enumPropertyName (EzIntegrationProviderId)
     * @param {string} enumPropertyName
     * A valid enum property name from EzIntegrationProviderId
     * @returns {string}
     * A valid enum property value from EzIntegrationType
     */
    static ezToEzIntegrationType(enumPropertyName) {
        return EzIntegrationType.ezValueOf(
            EzString.stringOrDefault(
                EzIntegrationProviderId.ezEnumData(enumPropertyName)?.ezIntegrationType,
                EzIntegrationType.UNKNOWN))
    }

    /**
     * @static
     * @public @method
     * Gets a string code id for the integration
     * @param {string} enumPropertyName
     * A valid enum property name for EzIntegrationProviderId
     * @returns {string}
     */
    static ezToCodeId(enumPropertyName) {
        return EzString.stringOrDefault(
            EzIntegrationProviderId.ezEnumData(enumPropertyName)?.ezCodeId,
            'UNKNOWN');
    }

    /**
     * @static
     * @public @method
     * Gets the integrations short name.
     * @param {string} enumPropertyName
     * A valid enum property name for EzIntegrationProviderId
     * @returns {string}
     */
    static ezToShortName(enumPropertyName) {
        return EzString.stringOrEmpty(EzIntegrationProviderId.ezEnumData(enumPropertyName)?.ezShortName);
    }

    /*
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Initialization Section: ONLY PLACE INITIALIZATION CODE BEYOND THIS SECTION
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */

    /**
     * @private @field
     * Stores the singleton instance of this enumeration class.
     * @type {EzIntegrationProviderId}
     * Internal static instance of the EzIntegrationProviderId
     */
    static #ezEnumerationSingleton = null;

    /**
      * @static
      * @private @method
      * Builds the enumeration properties and values for EzClockerAccountType
      */
    static #ezBuildEnumeration() {
        this.#ezEnumerationSingleton.ezBuildEnumProp(
            // Enumeration Property Name
            'UNKNOWN',
            // Enumeration Property Value
            'UNKNOWN',
            // Optional Enumeration Data Value
            {
                value: 'UNKNOWN',
                display: 'UNKNOWN',
                ezNovaAuthenticationProviderId: EzNovaAuthenticationProviderId.UNKNOWN,
                ezIntegrationType: 'UNKNOWN',
                ezShortName: 'UKN',
                ezCodeId: 'UNKNOWN',
                ezIntegrationHomeUrl: null,
                ezIntegrationLogoUrl: null,
                ezIntegrationLogoText: null,
                ezIntegrationSubTitleText: null,
                ezIntegrationSetupDialogApiName: null,
                ezIntegrationProductName: 'UNKNOWN',
                ezIntegrationProductHomeUrl: EzString.EMPTY,
                ezIntegrationProductImportSupportUrl: EzString.EMPTY,
                ezIntegrationSetupDialogId: null,
                ezIntegrationSetupNotes: null,
                ezIntegrationHelpUrl: null,
                ezAdditionalResourcesInfo: null,
                displayName: 'UNKNOWN'
            });

        this.#ezEnumerationSingleton.ezBuildEnumProp(
            // Enumeration Property Name
            'NONE',
            // Enumeration Property Value
            'NONE',
            // Optional Enumeration Data Value
            {
                value: 'NONE',
                display: 'None',
                ezNovaAuthenticationProviderId: EzNovaAuthenticationProviderId.UNKNOWN,
                ezIntegrationType: 'NONE',
                ezShortName: 'NONE',
                ezCodeId: 'NONE',
                ezIntegrationHomeUrl: null,
                ezIntegrationLogoUrl: null,
                ezIntegrationLogoText: null,
                ezIntegrationSubTitleText: null,
                ezIntegrationSetupDialogApiName: null,
                ezIntegrationProductName: 'NONE',
                ezIntegrationProductHomeUrl: EzString.EMPTY,
                ezIntegrationProductImportSupportUrl: EzString.EMPTY,
                ezIntegrationSetupDialogId: null,
                ezIntegrationSetupNotes: null,
                ezIntegrationHelpUrl: null,
                ezAdditionalResourcesInfo: null,
                displayName: 'None'
            });

        this.#ezEnumerationSingleton.ezBuildEnumProp(
            'QUICKBOOKS_ONLINE',
            'QUICKBOOKS_ONLINE',
            {
                value: 'QUICKBOOKS_ONLINE',
                display: 'Intuit QuickBooks Online',
                ezNovaAuthenticationProviderId: EzNovaAuthenticationProviderId.INTUIT,
                ezIntegrationType: EzIntegrationType.TIME_ENTRY_EXPORT,
                ezShortName: 'Intuit QBO',
                ezCodeId: 'IntuitQBO',
                ezIntegrationHomeUrl: 'https://intuit.com',
                ezIntegrationLogoUrl: '/public/images/integrations/intuit-qbo.svg',
                ezIntegrationLogoText: 'QuickBooks Online®',
                ezIntegrationSubTitleText: 'Direct Integration',
                ezIntegrationSetupDialogApiName: 'ezQBOIntegrationSetupDialog',
                ezIntegrationSetupDialogId: 'EzQBOIntegrationSetupDialog',
                ezIntegrationProductName: 'Intuit QuickBooks Online®',
                ezIntegrationProductHomeUrl: '',
                ezIntegrationProductImportSupportUrl: '',
                ezIntegrationSetupNotes: EzString.EMPTY,
                ezIntegrationHelpUrl: '/integrations/quickbooks-online',
                displayName: 'Intuit QuickBooks Online'
            });

        this.#ezEnumerationSingleton.ezBuildEnumProp(
            'QBO',
            'QBO',
            {
                value: 'QBO',
                display: 'Intuit QuickBooks Online',
                ezNovaAuthenticationProviderId: EzNovaAuthenticationProviderId.INTUIT,
                ezIntegrationType: EzIntegrationType.TIME_ENTRY_EXPORT,
                ezShortName: 'Intuit QBO',
                ezCodeId: 'IntuitQBO',
                ezIntegrationHomeUrl: EzString.EMPTY,
                ezIntegrationLogoUrl: EzString.EMPTY,
                ezIntegrationLogoText: 'QuickBooks Online®',
                ezIntegrationSubTitleText: 'Direct Integration',
                ezIntegrationSetupDialogApiName: EzString.EMPTY,
                ezIntegrationSetupDialogId: EzString.EMPTY,
                ezIntegrationProductName: 'Intuit QuickBooks Online®',
                ezIntegrationProductHomeUrl: EzString.EMPTY,
                ezIntegrationProductImportSupportUrl: EzString.EMPTY,
                ezIntegrationSetupNotes: EzString.EMPTY,
                ezIntegrationHelpUrl: '/integrations/quickbooks-online',
                ezAdditionalResourcesInfo: EzString.EMPTY,
                displayName: 'Intuit QuickBooks Online'
            });

        this.#ezEnumerationSingleton.ezBuildEnumProp(
            'XERO',
            'XERO',
            {
                value: 'XERO',
                display: 'Xero',
                ezNovaAuthenticationProviderId: EzNovaAuthenticationProviderId.NONE,
                ezIntegrationType: EzIntegrationType.TIME_ENTRY_EXPORT,
                ezShortName: 'Xero',
                ezCodeId: 'Xero',
                ezIntegrationHomeUrl: EzString.EMPTY,
                ezIntegrationLogoUrl: EzString.EMPTY,
                ezIntegrationLogoText: 'XERO®',
                ezIntegrationSubTitleText: 'CSV File Export',
                ezIntegrationSetupDialogApiName: EzString.EMPTY,
                ezIntegrationSetupDialogId: EzString.EMPTY,
                ezIntegrationProductName: 'Xero®',
                ezIntegrationProductHomeUrl: 'https://www.xero.com/us/',
                ezIntegrationProductImportSupportUrl: EzString.EMPTY,
                ezIntegrationSetupNotes: EzString.EMPTY,
                ezIntegrationHelpUrl: '/integrations/xero',
                ezAdditionalResourcesInfo: EzString.EMPTY,
                displayName: 'Xero'
            });

        this.#ezEnumerationSingleton.ezBuildEnumProp(
            'PAYCHEX',
            'PAYCHEX',
            {
                value: 'PAYCHEX',
                display: 'PAYCHEX',
                ezNovaAuthenticationProviderId: EzNovaAuthenticationProviderId.NONE,
                ezIntegrationType: EzIntegrationType.TIME_ENTRY_EXPORT_FILE,
                ezShortName: 'PAYCHEX®',
                ezCodeId: 'PayChex',
                ezIntegrationHomeUrl: 'https://www.paychex.com',
                ezIntegrationLogoUrl: '/public/images/integrations/paychex.svg',
                ezIntegrationLogoText: 'Flex® Payroll',
                ezIntegrationSubTitleText: 'CSV File Export',
                ezIntegrationSetupDialogApiName: 'ezPayChexIntegrationSetupDialog',
                ezIntegrationSetupDialogId: 'EzPayChexIntegrationSetupDialog',
                ezIntegrationProductName: 'Paychex Flex® Payroll',
                ezIntegrationProductHomeUrl: EzString.EMPTY,
                ezIntegrationProductImportSupportUrl: EzString.EMPTY,
                ezIntegrationSetupNotes: EzHtml.build`
                            <h2
                                id="EZ_PAYCHEX_Title">
                                EzClocker's Paychex Flex® Integration
                            </h2>
                            Export time period data to a CSV file and import into PAYCHEX
                            <h3
                                id="EZ_PAYCHEX_Requirements">
                                Requirements
                            </h3>
                            Your Paychex account must meet the following requirements to use the integration:
                            <ul
                                id="EZ_PAYCHEX_RequirementsList">
                                <li
                                    id="EZ_PAYCHEX_RequirementsList_Item1">
                                    A Paychex Flex plan is required to import time entries.
                                </li>
                                <li
                                    id="EZ_PAYCHEX_RequirementsList_Item2">
                                    The Paychex Standard Payroll Import setting needs enabled on your account.
                                </li>
                            </ul>
                            <h3
                                id="EZ_PAYCHEX_SetupInfo">
                                Setting Up the Integration
                            </h3>
                            Setting up the integration will require the following information from PAYCHEX:
                            <ul
                                id="EZ_PAYCHEX_SetupInfoList">
                                <li
                                    id="EZ_PAYCHEX_SetupInfoList_Item1">
                                    Your Paychex Client Id
                                </li>
                                <li
                                    id="EZ_PAYCHEX_SetupInfoList_Item2">
                                    The Worker Id for each employee
                                </li>
                                <li
                                    id="EZ_PAYCHEX_SetupInfoList_Item3">
                                    Your Paychex Pay Component Ids
                                </li>
                                <li
                                    id="EZ_PAYCHEX_SetupInfoList_Item4">
                                    Your Paychex Labor Assignment Ids
                                </li>
                            </ul>`,
                ezIntegrationHelpUrl: '/integrations/paychex',
                ezAdditionalResourcesInfo: EzString.EMPTY,
                displayName: 'PAYCHEX'
            });

        this.#ezEnumerationSingleton.ezBuildEnumProp(
            'ADP_WORKFORCE_NOW',
            'ADP_WORKFORCE_NOW',
            {
                value: 'ADP_WORKFORCE_NOW',
                display: 'ADP Workforce Now',
                ezNovaAuthenticationProviderId: EzNovaAuthenticationProviderId.NONE,
                ezIntegrationType: EzIntegrationType.TIME_ENTRY_EXPORT_FILE,
                ezShortName: 'ADP',
                ezCodeId: 'ADPWorkforceNow',
                ezIntegrationHomeUrl: 'https://www.adp.com',
                ezIntegrationLogoUrl: '/public/images/integrations/adp.svg',
                ezIntegrationLogoText: 'Workforce Now® Payroll',
                ezIntegrationSubTitleText: 'CSV File Export',
                ezIntegrationSetupDialogApiName: 'ezADPIntegrationSetupDialog',
                ezIntegrationSetupDialogId: 'EzADPIntegrationSetupDialog',
                ezIntegrationProductName: 'ADP Workforce Now®',
                ezIntegrationProductHomeUrl: 'https://www.adp.com/what-we-offer/products/adp-workforce-now.aspx',
                ezIntegrationProductImportSupportUrl: EzString.EMPTY,
                ezIntegrationSetupNotes: EzString.EMPTY,
                ezIntegrationHelpUrl: '/integrations/adp-workforce-now',
                ezAdditionalResourcesInfo: EzHtml.build`
                    <h2
                        id="EZ_ADP_WORKFORCE_NOW_AdditionalResources_Title">
                        Additional Resources for the ADP Workforce Now Payroll® Integration
                    </h2>
                    <p
                        id="EZ_ADP_WORKFORCE_NOW_AdditionalResources_Description">
                        To further help you successfully integrate ezClocker with ADP Workforce Now Payroll® we have
                        collected a list of external (not owned or maintained by ezClocker) websites that provide
                        additional information and help if you need it.
                    </p>
                    <ul
                        id="EZ_ADP_WORKFORCE_NOW_AdditionalResources_List">
                        <li
                            id="EZ_ADP_WORKFORCE_NOW_AdditionalResources_Website">
                            <a
                                id="EZ_ADP_WORKFORCE_NOW_AdditionalResources_WebsiteLink"
                                href="https://www.adp.com"
                                target="EZ_ADP_WORKFORCE_NOW_WEBSITE">
                                ADP Main Website
                            </a>
                        </li>
                        <li
                            id="EZ_ADP_WORKFORCE_NOW_AdditionalResources_InformationSite">
                            <a
                                id="EZ_ADP_WORKFORCE_NOW_AdditionalResources_InformationSiteLink"
                                href="https://www.adp.com/what-we-offer/products/adp-workforce-now/payroll.aspx"
                                target="EZ_ADP_WORKFORCE_NOW_InformationSite">
                                ADP Workforce Now Payroll Information Page
                            </a>
                        </li>
                    </ul>`,
                displayName: 'ADP Workforce Now'
            });

        this.#ezEnumerationSingleton.ezBuildEnumProp(
            'GUSTO',
            'GUSTO',
            {
                value: 'GUSTO',
                display: 'Gusto CSV',
                ezNovaAuthenticationProviderId: EzNovaAuthenticationProviderId.NONE,
                ezIntegrationType: EzIntegrationType.TIME_ENTRY_EXPORT_FILE,
                ezShortName: 'Gusto',
                ezCodeId: 'Gusto',
                ezIntegrationHomeUrl: 'https://gusto.com',
                ezIntegrationLogoUrl: '/public/images/integrations/gusto.svg',
                ezIntegrationLogoText: 'Payroll',
                ezIntegrationSubTitleText: 'CSV File Export',
                ezIntegrationSetupDialogApiName: 'ezGustoCSVIntegrationSetupDialog',
                ezIntegrationSetupDialogId: 'EzGustoCSVIntegrationSetupDialog',
                ezIntegrationProductName: 'Gusto Payroll® CSV',
                ezIntegrationProductHomeUrl: 'https://gusto.com/product/payroll',
                ezIntegrationProductImportSupportUrl: 'https://support.gusto.com/payroll/payroll-settings/CSV-Upload/999914471/Upload-payroll-data-with-a-CSV.htm',
                ezIntegrationSetupNotes: EzHtml.build`
                    <h2
                        id="EZ_GUSTO_CSV_Setup_Title">
                        EzClocker's Gusto Integration
                    </h2>
                    Export time period data to a CSV file and import into Gusto.
                    <h3
                        id="EZ_GUSTO_CSV_Setup_RequirementsH3">
                        Requirements
                    </h3>
                    <ul
                        id="EZ_GUSTO_CSV_Setup_RequirementsList">
                    </ul>
                    <h3
                        id="EZ_GUSTO_CSV_Setup_IntegrationSetupH3">
                        Integration Setup
                    </h3>
                    <ul
                        id="EZ_GUSTO_CSV_Setup_IntegrationSetupList">
                    </ul>`,
                ezIntegrationHelpUrl: '/integrations/gusto',
                ezAdditionalResourcesInfo: EzHtml.build`
                    <h2
                        id="EZ_GUSTO_CSV_AdditionalResources_Title">
                        Additional Resources for the Gusto Payroll® Integration
                    </h2>
                    <p
                        id="EZ_GUSTO_CSV_AdditionalResources_Description">
                        To further help you successfully integrate ezClocker with Gusto Payroll® we have collected a list
                        of external (not owned or maintained by ezClocker) websites that provide additional information
                        and help if you need it.
                    </p>
                    <ul
                        id="EZ_GUSTO_CSV_AdditionalResources_List">
                        <li
                            id="EZ_GUSTO_CSV_AdditionalResources_Website">
                            <a
                                id="EZ_CSV_GUSTO_AdditionalResources_WebsiteLink"
                                href="https://gusto.com"
                                target="EZ_GUSTO_CSV_Website">
                                Gusto Main Website
                            </a>
                        </li>
                        <li
                            id="EZ_GUSTO_CSV_AdditionalResources_InformationSite">
                            <a
                                id="EZ_GUSTO_CSV_AdditionalResources_InformationSiteLink"
                                href="https://gusto.com/product/payroll"
                                target="EZ_GUSTO_CSV_InformationSite">
                                Gusto Payroll Information Site
                            </a>
                        </li>
                        <li
                            id="EZ_GUSTO_CSV_AdditionalResources_HelpCenter">
                            <a
                                id="EZ_GUSTO_CSV_AdditionalResources_HelpCenterLink"
                                href="https://support.gusto.com/payroll/payroll-settings/CSV-Upload/999913061/Upload-hours-and-earnings-to-payroll.htm"
                                target="EZ_GUSTO_CSV_HelpCenter">
                                Gusto Help Center: Upload Hours and Earnings to Payroll
                            </a>
                        </li>
                    </ul>`,
                displayName: 'Gusto CSV'
            });

        this.#ezEnumerationSingleton.ezBuildEnumProp(
            'GUSTO_API',
            'GUSTO_API',
            {
                value: 'GUSTO_API',
                display: 'Gusto',
                ezNovaAuthenticationProviderId: EzNovaAuthenticationProviderId.GUSTO,
                ezIntegrationType: EzIntegrationType.TIME_ENTRY_EXPORT,
                ezShortName: 'Gusto',
                ezCodeId: 'GustoAPI',
                ezIntegrationHomeUrl: 'https://gusto.com',
                ezIntegrationLogoUrl: '/public/images/integrations/gusto.svg',
                ezIntegrationLogoText: 'Payroll',
                ezIntegrationSubTitleText: 'Direct Integration',
                ezIntegrationSetupDialogApiName: 'ezGustoIntegrationSetupDialog',
                ezIntegrationSetupDialogId: 'EzGustoIntegrationSetupDialog',
                ezIntegrationProductName: 'Gusto Payroll®',
                ezIntegrationProductHomeUrl: 'https://gusto.com/product/payroll',
                ezIntegrationProductImportSupportUrl: 'https://support.gusto.com/payroll/payroll-settings/CSV-Upload/999914471/Upload-payroll-data-with-a-CSV.htm',
                ezIntegrationSetupNotes: EzHtml.build`
                    <h2
                        id="EZ_GUSTO_API_Title">
                        EzClocker's Gusto Integration
                    </h2>
                    Export time period data to a CSV file and import into Gusto.
                    <h3
                        id="EZ_GUSTO_API_RequirementsH3">
                        Requirements
                    </h3>
                    <ul
                        id="EZ_GUSTO_API_RequirementsList">
                    </ul>
                    <h3
                        id="EZ_GUSTO_API_SetupH3">
                        Integration Setup
                    </h3>
                    <ul
                        id="EZ_GUSTO_API_SetupList">
                    </ul>`,
                ezIntegrationHelpUrl: '/integrations/gusto',
                ezAdditionalResourcesInfo: EzHtml.build`
                    <h2
                        id="EZ_GUSTO_API_AdditionalResourcesH2">
                        Additional Resources for the Gusto Payroll® Integration
                    </h2>
                    <p
                        id="EZ_GUSTO_API_AdditionalResourcesP">
                        To further help you successfully integrate ezClocker with Gusto Payroll® we have collected a list
                        of external (not owned or maintained by ezClocker) websites that provide additional information
                        and help if you need it.
                    </p>
                    <ul
                        id="EZ_GUSTO_API_AdditionalResourcesList">
                        <li
                            id="EZ_GUSTO_API_WebsiteLink">
                            <a
                                id="EZ_GUSTO_API_WebsiteLink"
                                href="https://gusto.com"
                                target="EZ_GUSTO_API_Website">
                                Gusto Main Website
                            </a>
                        </li>
                        <li
                            id="EZ_GUSTO_API_InformationSiteLink">
                            <a
                                id="EZ_GUSTO_API_InformationSiteLink"
                                href="https://gusto.com/product/payroll"
                                target="EZ_GUSTO_API_InformationSite">
                                Gusto Payroll Information Site
                            </a>
                        </li>
                        <li
                            id="EZ_GUSTO_API_HelpCenterLink">
                            <a
                                id="EZ_API_GUSTO_HelpCenterLink"
                                href="https://support.gusto.com/payroll/payroll-settings/CSV-Upload/999913061/Upload-hours-and-earnings-to-payroll.htm"
                                target="EZ_GUSTO_API_HelpCenter">
                                Gusto Help Center: Upload Hours and Earnings to Payroll
                            </a>
                        </li>
                    </ul>`,
                displayName: 'Gusto'
            });

        this.#ezEnumerationSingleton.ezBuildEnumProp(
            'ACS_TECHNOLOGIES',
            'ACS_TECHNOLOGIES',
            {
                value: 'ACS_TECHNOLOGIES',
                display: 'ACS Technologies',
                ezNovaAuthenticationProviderId: EzNovaAuthenticationProviderId.NONE,
                ezIntegrationType: EzIntegrationType.TIME_ENTRY_EXPORT_FILE,
                ezShortName: 'ACS',
                ezCodeId: 'ACSTechnologies',
                ezIntegrationHomeUrl: 'https://www.acstechnologies.com',
                ezIntegrationLogoUrl: '/public/images/integrations/acs-technologies-small.png',
                ezIntegrationLogoText: 'Payroll®',
                ezIntegrationSubTitleText: 'CSV File Export',
                ezIntegrationSetupDialogApiName: 'ezACSIntegrationSetupDialog',
                ezIntegrationSetupDialogId: 'EzACSIntegrationSetupDialog',
                ezIntegrationProductName: 'ACS Payroll®',
                ezIntegrationProductHomeUrl: 'https://www.acstechnologies.com/acs/church-accounting-software/acs-payroll',
                ezIntegrationProductImportSupportUrl: 'https://wiki.acstechnologies.com/display/ACSDOC/Importing+Time+into+ACS+Payroll',
                ezIntegrationSetupNotes: EzHtml.build`
                    <h2
                        id="EZ_ACS_TECHNOLOGIES_Title">
                        EzClocker's ACS Technologies Integration
                    </h2>
                    Export time period data to a CSV file and import into ACS Payroll®.
                    <h3
                        id="EZ_ACS_TECHNOLOGIES_RequirementsH3">
                        Requirements
                    </h3>
                    <ul
                        id="EZ_ACS_TECHNOLOGIES_RequirementsList">
                    </ul>
                    <h3
                        id="EZ_ACS_TECHNOLOGIES_SetupH3">
                        Integration Setup
                    </h3>
                    <ul
                        id="EZ_ACS_TECHNOLOGIES_SetupList">
                    </ul>`,
                ezIntegrationHelpUrl: '/integrations/acs-technologies',
                ezAdditionalResourcesInfo: EzHtml.build`
                    <h2
                        id="EZ_ACS_TECHNOLOGIES_AdditionalResourcesH2">
                        Additional Resources for the ACS Payroll® Integration
                    </h2>
                    <p
                        id="EZ_ACS_TECHNOLOGIES_AdditionalResourcesP">
                        To further help you successfully integrate ezClocker with ACS Payroll® we have collected a list
                        of external (not owned or maintained by ezClocker) websites that provide additional information
                        and help if you need it.
                    </p>
                    <ul
                        id="EZ_ACS_TECHNOLOGIES_AdditionalResourcesList">
                        <li>
                            <a
                                id="EZ_ACS_TECHNOLOGIES_WebsiteLink"
                                href="https://www.acstechnologies.com"
                                target="ACS_TECHNOLOGIES">
                                ACS Technologies Website
                            </a>
                        </li>
                        <li
                            id="EZ_ACS_TECHNOLOGIES_SupportLink">
                            <a
                                id="EZ_ACS_TECHNOLOGIES_SupportLink"
                                href="https://www.acstechnologies.com/expertise/support/"
                                target="ACS_TECHNOLOGIES">
                                ACS Technologies Support
                            </a>
                        </li>
                        <li
                            id="EZ_ACS_TECHNOLOGIES_InformationSiteLink">
                            <a
                                id="EZ_ACS_TECHNOLOGIES_InformationSiteLink"
                                href="https://www.acstechnologies.com/acs/church-accounting-software/acs-payroll/"
                                target="ACS_TECHNOLOGIES">
                                ACS Payroll Information Site
                            </a>
                        </li>
                        <li
                            id="EZ_ACS_TECHNOLOGIES_DocumentationWikiLink">
                            <a
                                id="EZ_ACS_TECHNOLOGIES_DocumentationWikiLink"
                                href="https://wiki.acstechnologies.com/display/ACSDOC/Payroll"
                                target="ACS_TECHNOLOGIES">
                                ACS Payroll Documentation Wiki
                            </a>
                        <li
                            id="EZ_ACS_TECHNOLOGIES_ImportingTimeIntoACSPayrollLink">
                            <a
                                id="EZ_ACS_TECHNOLOGIES_ImportingTimeIntoACSPayrollLink"
                                href="https://wiki.acstechnologies.com/display/ACSDOC/Importing+Time+into+ACS+Payroll"
                                target="ACS_TECHNOLOGIES">
                                ACS Technologies Documentation on Importing Time into ACS Payroll
                            </a>
                        </li>
                    </ul>`,
                displayName: 'ACS Technologies'
            });

        this.#ezEnumerationSingleton.ezBuildEnumProp(
            'PAYCOM',
            'PAYCOM',
            {
                value: 'PAYCOM',
                display: 'Paycom',
                ezNovaAuthenticationProviderId: EzNovaAuthenticationProviderId.NONE,
                ezIntegrationType: EzIntegrationType.TIME_ENTRY_EXPORT_FILE,
                ezShortName: 'Paycom®',
                ezCodeId: 'Paycom',
                ezIntegrationHomeUrl: 'https://www.paycom.com',
                ezIntegrationLogoUrl: '',
                ezIntegrationLogoText: 'Paycom Payroll',
                ezIntegrationSubTitleText: 'CSV File Export',
                ezIntegrationSetupDialogApiName: 'ezPaycomIntegrationSetupDialog',
                ezIntegrationSetupDialogId: 'EzPaycomIntegrationSetupDialog',
                ezIntegrationProductName: 'Paycom Payroll',
                ezIntegrationProductHomeUrl: EzString.EMPTY,
                ezIntegrationProductImportSupportUrl: EzString.EMPTY,
                ezIntegrationSetupNotes: EzHtml.build`
                    <h2
                        id="EZ_PAYCOM_NotesH2">
                        EzClocker Paycom® Integration
                    </h2>
                    Export time period data to a CSV file and import into Paycom.
                    <h3
                        id="EZ_PAYCOL_SettingUpIntegrationH3">
                        Setting Up the Integration
                    </h3>`,
                ezIntegrationHelpUrl: '/integrations/paycom',
                ezAdditionalResourcesInfo: EzString.EMPTY,
                displayName: 'Paycom'
            });
    }

    /**
     * @static
     * Static Initialization
     */
    static {
        if (null == this.#ezEnumerationSingleton) {
            this.#ezEnumerationSingleton = new EzIntegrationProviderId();

            this.#ezBuildEnumeration();
        }
    }

    /*
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * End of Initialziation Section: !! DO NOT PLACE ANY CODE BEYOND THIS SECTION !!
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */
}
